/**
 * NewDispatchLauncher (BOARD-DBLCLICK-NEW, Seth 2026-08-12) — double-click empty
 * space in a tech's column → start a NEW dispatch right there, the way ESC's
 * board does, but better: the click already knows the TECH and the DAY, so both
 * arrive prefilled. Step 1 finds the customer (the one thing the board doesn't
 * know); step 2 is the normal CreateDispatchModal, which the dispatcher can
 * still fully change — prefill is a head start, never a cage.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarPlus, ChevronRight, MapPin, Phone, Search, UserPlus, UserRound } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";
import { formatDateUS } from "@/lib/format";
import { useCustomerSearch, type CustomerWithLocations, type LocationRow } from "@/features/customer-360/api";
import { CreateDispatchModal } from "@/features/customer-360/CreateDispatchModal";
import { AddCustomerModal } from "@/features/customer-360/AddCustomerModal";

interface PickedCustomer {
  custno: string;
  locno: string | null;
  name: string;
  address: string | null;
  city: string | null;
  phone: string | null;
}

/** One selectable row = one customer LOCATION (multi-location accounts fan out). */
function locationRows(c: CustomerWithLocations): { loc: LocationRow | null; multi: boolean }[] {
  if (!c.locations || c.locations.length === 0) return [{ loc: null, multi: false }];
  return c.locations.map((l) => ({ loc: l, multi: c.locations.length > 1 }));
}

function displayName(c: CustomerWithLocations): string {
  return (
    c.fullname?.trim() ||
    [c.firstname, c.lastname].filter(Boolean).join(" ").trim() ||
    c.custno
  );
}

export function NewDispatchLauncher({
  empno,
  techName,
  date,
  onClose,
}: {
  /** The lane that was double-clicked — prefills the technician. */
  empno: string | null;
  techName?: string | null;
  /** The board date — prefills the promised day. */
  date: string;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [picked, setPicked] = useState<PickedCustomer | null>(null);
  // NEW-CALLER-01 (Seth 2026-09-30): a brand-new caller shouldn't dead-end
  // the booking — create them right here, then keep booking.
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query), 300);
    return () => window.clearTimeout(t);
  }, [query]);
  useEffect(() => inputRef.current?.focus(), []);

  const { data: hits, isFetching } = useCustomerSearch(debounced);
  const rows = useMemo(
    () =>
      (hits ?? []).flatMap((c) =>
        locationRows(c).map(({ loc, multi }) => ({ c, loc, multi })),
      ),
    [hits],
  );

  // NEW-CALLER-01 — someone new to Sedgwick: the same Add-a-Customer modal,
  // prefilled from whatever was typed (it knows a phone from an address from
  // a name), and creating flows STRAIGHT back into booking this dispatch.
  if (creating) {
    return (
      <AddCustomerModal
        initialQuery={debounced}
        onClose={() => setCreating(false)}
        onCreated={(c) =>
          setPicked({
            custno: c.custno,
            locno: null,
            name: c.name,
            address: c.address,
            city: c.city,
            phone: c.phone,
          })
        }
      />
    );
  }

  // Step 2 — the real builder, with the board's tech + day already in place.
  if (picked) {
    return (
      <CreateDispatchModal
        custno={picked.custno}
        locno={picked.locno}
        customerName={picked.name}
        address={picked.address}
        city={picked.city}
        phone={picked.phone}
        prefillEmpno={empno}
        prefillDate={date}
        onClose={onClose}
      />
    );
  }

  // Step 1 — who's this call for?
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      icon={<CalendarPlus className="h-4 w-4 text-brand-500" />}
      title="New dispatch"
      subtitle={`${techName || (empno ? `Tech ${empno}` : "Unassigned")} · ${new Date(date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" })} ${formatDateUS(date)}`}
    >
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find the customer — name, address, or phone…"
            className="w-full rounded-lg border border-hairline py-2.5 pl-9 pr-3 text-[15px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <div className="max-h-80 overflow-auto rounded-lg border border-hairline">
          {debounced.trim().length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              Start typing to search customers.
            </div>
          ) : rows.length === 0 && !isFetching ? (
            <div className="px-4 py-7 text-center">
              <p className="text-sm text-slate-400">No customers match “{debounced}”.</p>
              {/* NEW-CALLER-01: the dead end becomes the door. */}
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-700 px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-brand-800"
              >
                <UserPlus className="h-4 w-4" /> New Customer — start from “{debounced.length > 28 ? debounced.slice(0, 28) + "…" : debounced}”
              </button>
              <p className="mx-auto mt-2 max-w-xs text-[11px] leading-snug text-slate-400">
                What you typed rides along — then you land right back here with them picked, booking this call.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-hairline">
              {rows.slice(0, 30).map(({ c, loc, multi }) => {
                const name = displayName(c);
                const addr = loc?.add1 ?? c.add1;
                const city = loc?.city ?? c.city;
                const phone = c.phone1 ?? loc?.phone1 ?? null;
                return (
                  <button
                    key={`${c.custno}-${loc?.locno ?? "x"}`}
                    type="button"
                    onClick={() =>
                      setPicked({
                        custno: c.custno,
                        locno: loc?.locno ?? null,
                        name,
                        address: addr ?? null,
                        city: city ?? null,
                        phone,
                      })
                    }
                    className="group flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-brand-50/60"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 group-hover:bg-brand-100 group-hover:text-brand-700">
                      <UserRound className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span className="truncate text-sm font-semibold text-slate-900">{name}</span>
                        <span className="shrink-0 text-[11px] text-slate-400">#{c.custno}</span>
                        {multi && loc?.locname && (
                          <span className="truncate rounded bg-slate-100 px-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                            {loc.locname}
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                        {(addr || city) && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-slate-400" />
                            {[addr, city].filter(Boolean).join(", ")}
                          </span>
                        )}
                        {phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="h-3 w-3 text-slate-400" /> {phone}
                          </span>
                        )}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-brand-500" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className={cn("min-w-0 text-[11px] text-slate-400", isFetching && "animate-pulse")}>
            {isFetching
              ? "Searching…"
              : "Pick the customer — the tech and day are already filled from where you clicked."}
          </p>
          {/* NEW-CALLER-01: near-miss matches happen too — the door stays open. */}
          {rows.length > 0 && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11.5px] font-semibold text-brand-700 transition-colors hover:bg-brand-50"
            >
              <UserPlus className="h-3.5 w-3.5" /> None of these — New Customer
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
